import React from 'react';
import { motion } from 'framer-motion';
import urlJoin from 'url-join';
import { useAuth0 } from 'Auth';
import { SecondaryMenuLink } from 'Shared/components/SecondaryMenu';
import { Can } from 'Shared/utils/auth';
import {
  managePartnerPermissionsPolicy,
  reattributePolicies,
} from 'Admin/authPolicies';
import styles from './styles.module.css';
import { env } from 'Shared/constants/env';
import { PartnerType } from 'Admin/Partners';

export const PartnerMenu: React.FC<{
  baseUrl: string;
  partner: PartnerType;
}> = ({ baseUrl, partner }) => {
  const { user } = useAuth0();

  const partnerMenuItems = [
    {
      name: 'Details',
      path: urlJoin(baseUrl, `/partners/${partner.id}/details`),
      testId: 'partner-menu-details-link',
    },
    {
      name: 'Permissions',
      path: urlJoin(baseUrl, `/partners/${partner.id}/permissions`),
      testId: 'partner-menu-permissions-link',
      permissionPolicy: managePartnerPermissionsPolicy,
    },
    {
      name: 'Commissions',
      path: urlJoin(baseUrl, `/partners/${partner.id}/commissions`),
      testId: 'partner-menu-commissions-link',
      // Carrier check will move to permissions policy when new roles & permissions are done
      featureFlag: env.partnerCommissionsAdminMenuEnabled && !partner.isCarrier,
    },
    {
      name: 'Compliance',
      path: urlJoin(baseUrl, `/partners/${partner.id}/compliance`),
      testId: 'partner-menu-compliance-link',
    },
    {
      name: 'Policy Reattribution',
      path: urlJoin(baseUrl, `/partners/${partner.id}/reattribution`),
      testId: 'partner-menu-reattribution-link',
      permissionPolicy: reattributePolicies,
    },
    {
      name: 'Agencies',
      path: urlJoin(baseUrl, `/partners/${partner.id}/agencies`),
      testId: 'partner-menu-agencies-link',
    },
    {
      name: 'Users',
      path: urlJoin(baseUrl, `/partners/${partner.id}/users`),
      testId: 'partner-menu-users-link',
    },
  ];

  const navVariants = {
    closed: {
      y: -10,
      height: 0,
      opacity: 0,
      transition: { when: 'afterChildren' },
    },
    open: {
      transition: { duration: 0.2 },
      height: 'auto',
      y: 0,
      opacity: 1,
    },
    exit: {
      transition: { duration: 0.2 },
      height: 0,
      y: 10,
      opacity: 0,
    },
  };

  return (
    <motion.nav
      aria-label="Partner Admin Navigation"
      role="navigation"
      animate="open"
      initial="closed"
      exit="exit"
      key="partnerMenu"
      variants={navVariants}
      className={styles.container}
      data-testid="partner-menu"
    >
      <div className={styles.partner} data-testid="partner-menu-name">
        {partner.name}
      </div>
      {partnerMenuItems.map((item) => {
        if (item.featureFlag === false) return null;
        return item.permissionPolicy ? (
          <Can
            key={item.name}
            policy={item.permissionPolicy}
            grants={user!.effectiveGrants}
            context={partner}
            yes={() => <SecondaryMenuLink item={item} />}
          />
        ) : (
          <SecondaryMenuLink key={item.name} item={item} />
        );
      })}
      <hr />
    </motion.nav>
  );
};
